import type { ValueFormat } from "@kaleidoscope-solutions/engine-wasm"
import type { FunctionalComponent } from "vue"
import type { InteractionInfo } from "./fileImport/interactions"
import type { CSVObj } from "@/types/fileImport"
import type { DatasetType } from "@/utils/useEngineModel/ModelFacade"
import {
  BlockIcon,
  DatasetCalcIcon,
  DatasetNumberIcon,
  DatasetSelectIcon,
  DatasetTextIcon,
} from "@kaleidoscope-solutions/ks-icons/vue/light"
import { captureException } from "@sentry/vue"
import { useDateFormat } from "@vueuse/core"
import { saveAs } from "file-saver"
import JSZip from "jszip"
import Papa from "papaparse"
import { Decorator } from "@/types/index"
import { DirtyState } from "./fileImport/interactions"

export const DUMMY_UUID = "00000000-0000-4000-0000-000000000000"

export function randomId() {
  return `id${(Math.random() + 1).toString(36).substring(7)}`
}

export function slugify(str: string) {
  return str
    .toString()
    .normalize("NFD") // split an accented letter in the base letter and the accent
    .replace(/[\u0300-\u036F]/g, "") // remove all previously split accents
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9 ]/g, "") // remove all chars not letters, numbers and spaces (to be replaced)
    .replace(/\s+/g, "-")
}

export async function promptDownload(name: string, chunks: string[], fileNames: string[]): Promise<void> {
  const zip = new JSZip()
  for (let index = 0; index < chunks.length; index++) {
    const chunk = chunks[index]
    const fileName = fileNames[index]
    zip.file(fileName, chunk)
  }

  try {
    const content = await zip.generateAsync({ type: "blob" })
    saveAs(content, name)
  } catch (error) {
    captureException(error)
    console.error("Error generating zip file:", error)
  }
}

export function formatFileSize(bytes: number | string, decimals = 2): string {
  const k = 1000
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"]

  if (typeof bytes === "string") {
    bytes = Number(bytes)
  }

  if (bytes === 0) {
    return "0 Byte"
  }

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${Number((bytes / k ** i).toFixed(decimals))} ${sizes[i]}`
}

export function formatChartNumber(value: number, valueFormat: ValueFormat = { style: "None" }, compact?: boolean): string {
  const options: Intl.NumberFormatOptions = { }

  if (compact) {
    options.notation = "compact"
  }

  if (valueFormat.style === "Percent") {
    options.style = "percent"
  } else if (valueFormat.style === "Currency") {
    options.style = "currency"
    options.currency = valueFormat.content
    options.currencyDisplay = "narrowSymbol" // Remove US from US$ when locate isn't en-US
    options.maximumFractionDigits = 0
  } else {
    options.minimumFractionDigits = 0
    // Show decimals for small numbers
    options.maximumFractionDigits = value < 10 ? 2 : 0
  }

  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
  return Intl.NumberFormat(
    undefined, // The runtime's default locale is used when undefined is passed
    options
  ).format(value)
}

export function formatDate(
  date: number | string,
  format = "YYYY-MM-DD HH:mm:ss"
): string {
  const formattedDate = useDateFormat(date, format)
  return formattedDate.value
}

/**
 *
 * @param weekStr Should be 2025-W30
 * @returns Date | null
 */
function isoWeekStringToDate(weekStr: string): Date | null {
  const match = weekStr.match(/^(\d{4})-W(\d{2})$/)
  if (!match) {
    return null
  }

  const year = Number.parseInt(match[1], 10)
  const week = Number.parseInt(match[2], 10)

  return getDateOfISOWeek(week, year)
}

export function sortIsoWeekString(a: string, b: string): number {
  const aDate = isoWeekStringToDate(a)
  const bDate = isoWeekStringToDate(b)

  if (aDate && bDate) {
    return aDate.getTime() - bDate.getTime()
  }

  return 0
}

function getDateOfISOWeek(week: number, year: number): Date {
  const simple = new Date(year, 0, 4) // Jan 4 is always in week 1
  const dayOfWeek = simple.getDay() || 7 // make Sunday (0) become 7
  const ISOWeekStart = new Date(simple)
  ISOWeekStart.setDate(simple.getDate() - dayOfWeek + 1 + (week - 1) * 7)
  return ISOWeekStart
}

function formatWeekCommencing(date: Date): string {
  const formatted = date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric"
  })

  return `wc ${formatted}`
}

export function isoWeekStringToWeekCommencing(weekStr: string) {
  const date = isoWeekStringToDate(weekStr)
  if (date === null) {
    return ""
  }
  return formatWeekCommencing(date)
}

export function objectToCsv(obj: CSVObj): string {
  return Papa.unparse([obj.headers, ...obj.rows])
}

export function datasetIconMap(type: DatasetType) {
  const iconMap: Record<DatasetType, FunctionalComponent> = {
    Boolean: DatasetNumberIcon,
    Text: DatasetTextIcon,
    Number: DatasetNumberIcon,
    Calculation: DatasetCalcIcon,
    List: DatasetSelectIcon,
  }
  return iconMap[type] ?? BlockIcon
}

export function sortObjectsByProperty(array: any[], property: string) {
  const sortedArray = [...array]

  sortedArray.sort((a, b) => {
    if (a[property] < b[property]) {
      return -1
    }
    if (a[property] > b[property]) {
      return 1
    }
    return 0
  })

  return sortedArray
}

export function removeRelationshipPrefix(
  datasetName: string,
  prefix: string
): string {
  return datasetName.replace(`${prefix}::`, "")
}

/**
 *
 * @param datasetName
 * @returns string
 */
export function removeDatasetPrefix(datasetName: string): string {
  return datasetName.replace(/.+::/, "")
}

/**
 *
 * @param listName
 * @returns string
 */
export function removeListSuffix(listName: string): string {
  return listName.replace("::All", "")
}

/**
 *
 * @param name
 * @returns string
 */
export function removeSuffixAndPrefix(name: string): string {
  return removeDatasetPrefix(removeListSuffix(name))
}

export function stringArrayToLower(strings: string[]): string[] {
  return strings.map((string) => string.toLowerCase())
}

export function stringKeyedMapToLower<T>(map: Map<string, T>): Map<string, T> {
  const lowerCaseMap = new Map<string, T>()
  for (const [key, value] of map) {
    lowerCaseMap.set(key.toLowerCase(), value)
  }

  return lowerCaseMap
}

// Given a before and after list, return two new lists.
// The 1st are new things which have been added.
// The 2nd are things which have been removed.
export function listDiff<T>(before: T[], after: T[]): [T[], T[]] {
  const added = after.filter((x) => !before.includes(x))
  const removed = before.filter((x) => !after.includes(x))
  return [added, removed]
}

export function incrementLastCharacter(str: string) {
  // Convert the last character to its Unicode value
  const lastCharCode = str.charCodeAt(str.length - 1)

  // Increment the Unicode value by 1
  const newLastCharCode = lastCharCode + 1

  // Convert the new Unicode value back to a character
  const newLastCharacter = String.fromCharCode(newLastCharCode)

  // Replace the last character in the string with the new character
  return str.slice(0, -1) + newLastCharacter
}

// Currently used by both file import and Integrations
export function getDecorator(
  interaction: InteractionInfo | undefined,
  valid: boolean
): Decorator {
  if (!interaction) {
    return Decorator.NOT_APPLICABLE
  }

  if (valid && interaction.getDirty() === DirtyState.Guess) {
    return Decorator.VALID_GUESS
  }

  if (valid) {
    return Decorator.VALID
  }

  if (interaction.isTouched()) {
    return Decorator.INVALID
  }

  return Decorator.NOT_APPLICABLE
}

/**
 * A simple and naive way to pluralise a word
 * @param word
 * @returns string
 */
export function simplePlural(word: string) {
  if (typeof word !== "string") {
    return ""
  }
  if (word.endsWith("y")) {
    return `${word.slice(0, -1)}ies`
  } else if (word.endsWith("s")) {
    return `${word}es`
  } else {
    return `${word}s`
  }
}

export function simplePluralWithCount(count: number, singular: string, plural?: string) {
  if (count === 1) {
    return `${count} ${singular}`
  } else {
    return `${count} ${plural ?? simplePlural(singular)}`
  }
}
